import { vec3 } from 'gl-matrix';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { STRUCTURE_SET_NOT_SHOWN_HINT_ID } from '~/hooks/target/structureSetNotShown';
import {
    currenPositionAutoScrollState,
    isAutoScrollingState,
} from '~/state/atoms/viewerAtoms';
import { canSweep, SliceSweep, sliceSweepOf, structureOf } from './sliceSweep';
import { useSliceSweepTarget } from './useSliceSweepTarget';
import type { RoiId } from '~/domain/changes/ids';

/** Speed of the sweep: one slice width per this many seconds (~7.1 slices/s). */
const SECONDS_PER_SLICE = 0.14;
/** Longest frame interval the sweep advances for, so a tab switch cannot jump it. */
const MAX_FRAME_SECONDS = 0.1;

/** Why a sweep cannot be asked for: the structure has no extent to sweep. */
export const NOTHING_TO_SCROLL_HINT_ID = 'tool.disabled.nothingToScroll';

/**
 * Sweeps the displayed slice back and forth through a structure: from the
 * structure's near face along the sweep axis to its far face and back, at a
 * steady slice rate. The sweep moves the plane intersection point of the
 * sweep target, the Environment that holds the active structure set, so
 * every view of that Environment, in any row, follows. A running sweep
 * keeps its slice and its direction while any view shows the Environment,
 * however its views are replaced or reordered.
 *
 * `canScroll` says whether a sweep can be asked for, and `blockedReasonId`
 * why not: the set is on no view, or the structure has nothing to sweep.
 * The sweeping state says a sweep is running: `startScrolling` computes the
 * sweep once (building the structure's texture if it has none, a user
 * action) and starts it only when there is one, and a running sweep whose
 * target goes away stops and clears the state, so a Play control never
 * shows Stop while nothing moves.
 *
 * @param roiId The structure to sweep through.
 */
export const useAutoScroll = (roiId: RoiId) => {
    const [shouldScroll, setShouldScroll] =
        useRecoilState(isAutoScrollingState);
    const setCurrentPosition = useSetRecoilState(currenPositionAutoScrollState);
    const animationFrameRef = useRef<number | null>(null);
    const scrollDirectionRef = useRef<number>(1);
    const positionRef = useRef<vec3 | null>(null);
    const lastTimestampRef = useRef<number | null>(null);
    // The sweep Play computed, taken by the effect that starts it, so the
    // structure's bounds are scanned once per press.
    const pendingSweepRef = useRef<SliceSweep | null>(null);
    const { environment, axis, shown } = useSliceSweepTarget();
    const roi = structureOf(environment, roiId);
    const builtTexture = roi?.builtTexture;

    const canScroll = useMemo(
        () => shown && canSweep(roi, axis, builtTexture),
        [shown, roi, axis, builtTexture]
    );
    const blockedReasonId = canScroll
        ? undefined
        : !environment || !shown
          ? STRUCTURE_SET_NOT_SHOWN_HINT_ID
          : NOTHING_TO_SCROLL_HINT_ID;

    useEffect(() => {
        if (!shouldScroll) {
            return;
        }
        const sweep =
            pendingSweepRef.current ?? sliceSweepOf(roi, axis, roi?.texture);
        pendingSweepRef.current = null;
        if (!sweep || !environment || !shown) {
            setShouldScroll(false);
            return;
        }
        const { start, end, stepMm } = sweep;

        scrollDirectionRef.current = 1;
        lastTimestampRef.current = null;
        positionRef.current = vec3.clone(start);
        setCurrentPosition(vec3.clone(start));

        const speed = stepMm / SECONDS_PER_SLICE; // mm per second
        const scrollLine = vec3.sub(vec3.create(), end, start);
        const scrollLineDot = vec3.dot(scrollLine, scrollLine);
        // Travel direction of the sweep: from `start` (the structure's near
        // face) toward `end`.
        const scrollDir = vec3.normalize(vec3.create(), scrollLine);

        const scroll = (timestamp: number) => {
            if (!positionRef.current) {
                return;
            }

            if (lastTimestampRef.current === null) {
                lastTimestampRef.current = timestamp;
            }
            const dt = Math.min(
                (timestamp - lastTimestampRef.current) / 1000,
                MAX_FRAME_SECONDS
            );
            lastTimestampRef.current = timestamp;

            const displacement = speed * dt * scrollDirectionRef.current;

            const newPosition = vec3.scaleAndAdd(
                vec3.create(),
                positionRef.current,
                scrollDir,
                displacement
            );

            const scrollDistance = vec3.dot(
                vec3.sub(vec3.create(), newPosition, start),
                scrollLine
            );

            if (scrollDistance < 0) {
                scrollDirectionRef.current = 1;
                vec3.copy(newPosition, start);
            } else if (scrollDistance > scrollLineDot) {
                scrollDirectionRef.current = -1;
                vec3.copy(newPosition, end);
            }

            positionRef.current = newPosition;
            setCurrentPosition(vec3.clone(newPosition));
            environment.updatePlaneIntersectionPoint(newPosition);

            animationFrameRef.current = requestAnimationFrame(scroll);
        };

        animationFrameRef.current = requestAnimationFrame(scroll);

        return () => {
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [
        shouldScroll,
        environment,
        roi,
        axis,
        shown,
        setShouldScroll,
        setCurrentPosition,
    ]);

    const startScrolling = useCallback(() => {
        if (!shown) {
            return;
        }
        const sweep = sliceSweepOf(roi, axis, roi?.texture);
        if (!sweep) {
            return;
        }
        pendingSweepRef.current = sweep;
        setShouldScroll(true);
    }, [shown, roi, axis, setShouldScroll]);

    const stopScrolling = useCallback(
        () => setShouldScroll(false),
        [setShouldScroll]
    );

    return {
        shouldScroll,
        canScroll,
        blockedReasonId,
        startScrolling,
        stopScrolling,
    };
};
